import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useDevBypassRedirect } from "@/hooks/use-dev-bypass-redirect";
import { track } from "@/lib/track";
import {
  NanaeLogoFull,
  NanaeLogoSymbol,
  NanaeWordmark,
} from "@/components/qv3";
import { QMono } from "@/components/qv3/QMono";
import {
  AnthropicLogo,
  OpenAILogo,
  GeminiLogo,
  MistralLogo,
  DeepSeekLogo,
  PerplexityLogo,
} from "@/components/brand/llm-logos";
import { CohereLogo } from "@/components/brand/llm-logos/Cohere";
import { PersonaProvider } from "@/components/qorum/PersonaProvider";

import pDelphine from "@/assets/landing-v3/p-delphine.jpg";
import pHenri from "@/assets/landing-v3/p-henri.jpg";
import pEva from "@/assets/landing-v3/p-eva.jpg";
import pAkilah from "@/assets/landing-v3/p-akilah.jpg";
import pJulien from "@/assets/landing-v3/p-julien.jpg";
import pRen from "@/assets/landing-v3/p-ren.jpg";
import pImani from "@/assets/landing-v3/p-imani.jpg";
import pPazuru from "@/assets/landing-v3/p-pazuru.jpg";
import memoImg from "@/assets/landing-v3/memo.jpg";
import { socialMeta } from "@/lib/seo-meta";

/* ===================== FAQ (contenu + données structurées) ===================== */
const FAQ_ITEMS: Array<{ q: string; a: string }> = [
  { q: "Qu'est-ce qui distingue Hōjin d'un ChatGPT classique ?", a: "ChatGPT vous donne une réponse : celle d'une seule intelligence, depuis un seul point de vue. Hōjin réunit sept modèles d'IA différents (OpenAI, Anthropic, Google, Mistral, et d'autres) qui délibèrent et se contredisent réellement, comme un vrai conseil d'administration. Là où une IA seule cherche à vous plaire, le Conseil de Hōjin cherche vos angles morts — l'un des cadres a même pour rôle d'attaquer le consensus. Vous ne recevez pas une réponse lisse, mais un verdict éprouvé par la contradiction." },
  { q: "À qui s'adresse Hōjin ?", a: "À tous ceux qui prennent des décisions importantes sans comité pour les challenger : dirigeants de PME et de TPE, entrepreneurs, indépendants, porteurs de projet. Face à un choix à fort enjeu — lancement, pricing, levée, rachat, pivot, investissement — et avec un seul point de vue, Hōjin vous apporte la délibération contradictoire qui manque à votre décision." },
  { q: "Combien ça coûte ?", a: "Hōjin propose plusieurs formules selon votre usage : Découverte à 9,99€/mois (5 délibérations), Essentiel à 19,99€/mois (12 délibérations) et Pro à 29,99€/mois (20 délibérations). Vous préférez sans engagement ? Chaque délibération est disponible à la carte pour 2,99€. Et une première délibération est offerte pour découvrir. Sept modèles d'IA et une recherche documentaire approfondie à chaque délibération — une profondeur d'analyse qu'aucun conseil ponctuel n'égale à ce tarif." },
  { q: "Pourquoi sept IA et pas une seule ?", a: "Parce que la valeur d'un conseil ne vient pas d'une réponse, mais de la confrontation des points de vue. Une seule IA, même excellente, raisonne depuis un unique biais : elle ne peut pas se contredire de façon crédible. Sept modèles de fournisseurs différents apportent sept angles — et c'est leur désaccord qui fait émerger ce qu'un point de vue unique aurait manqué. La différence entre demander son avis à une personne et réunir un véritable conseil." },
  { q: "Hōjin remplace-t-il mon avocat ou mon expert-comptable ?", a: "Non — et c'est volontaire. Hōjin ne fournit aucun conseil juridique, fiscal ou comptable, réservé aux professionnels habilités. Son rôle est de vous préparer à vos rendez-vous : le mode Audit examine ce que votre conseil vous propose et vous indique les bonnes questions à lui poser, les angles à ne pas oublier. Vous arrivez mieux préparé chez votre avocat ou votre expert-comptable — pas pour les remplacer, mais pour tirer le meilleur de leur expertise." },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Hōjin — Sept intelligences. Une clarté." },
      {
        name: "description",
        content:
          "Hōjin est une intelligence artificielle conçue dans la philosophie japonaise du lotus. Sept IA distinctes débattent pour vos décisions stratégiques.",
      },
      ...socialMeta({
        title: "Hōjin — Sept intelligences. Une clarté.",
        description: "Hōjin est une intelligence artificielle conçue dans la philosophie japonaise du lotus. Sept IA distinctes débattent pour vos décisions stratégiques.",
        url: "https://hojin.ai/",
        image: "conseil",
        card: "summary_large_image",
      }),
    ],

    links: [{ rel: "canonical", href: "https://hojin.ai/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://hojin.ai/#organization",
              name: "Hōjin",
              legalName: "UDITEK SAS",
              alternateName: "Hōjin AI",
              url: "https://hojin.ai/",
              logo: {
                "@type": "ImageObject",
                "@id": "https://hojin.ai/#logo",
                url: "https://hojin.ai/favicon-512.png",
                width: 512,
                height: 512,
                caption: "Hōjin",
              },
              image: { "@id": "https://hojin.ai/#logo" },
              description:
                "Conseil de sept intelligences artificielles qui débattent contradictoirement pour éclairer vos décisions stratégiques.",
              slogan: "Sept voix, une direction.",
              areaServed: "FR",
              knowsLanguage: ["fr-FR"],
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "customer support",
                  email: "contact@hojin.ai",
                  availableLanguage: ["French"],
                },
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://hojin.ai/#website",
              name: "Hōjin",
              url: "https://hojin.ai/",
              publisher: { "@id": "https://hojin.ai/#organization" },
              inLanguage: "fr-FR",
            },
            {
              "@type": "WebPage",
              "@id": "https://hojin.ai/#webpage",
              url: "https://hojin.ai/",
              name: "Hōjin — Sept intelligences. Une clarté.",
              description:
                "Hōjin réunit sept intelligences artificielles en Conseil : elles débattent, se contredisent et rendent un verdict argumenté sur vos décisions stratégiques.",
              isPartOf: { "@id": "https://hojin.ai/#website" },
              about: { "@id": "https://hojin.ai/#organization" },
              inLanguage: "fr-FR",
              primaryImageOfPage: { "@id": "https://hojin.ai/#logo" },
            },
            {
              "@type": "SoftwareApplication",
              "@id": "https://hojin.ai/#application",
              name: "Hōjin",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              url: "https://hojin.ai/",
              provider: { "@id": "https://hojin.ai/#organization" },
              description:
                "Délibération contradictoire de sept modèles d'IA (OpenAI, Anthropic, Google, Mistral, DeepSeek, Perplexity, Cohere) aboutissant à un verdict sourcé.",
              inLanguage: "fr-FR",
              offers: [
                {
                  "@type": "Offer",
                  name: "À la carte",
                  price: "2.99",
                  priceCurrency: "EUR",
                  url: "https://hojin.ai/pricing",
                },
                {
                  "@type": "Offer",
                  name: "Découverte",
                  price: "9.99",
                  priceCurrency: "EUR",
                  url: "https://hojin.ai/pricing",
                },
                {
                  "@type": "Offer",
                  name: "Essentiel",
                  price: "19.99",
                  priceCurrency: "EUR",
                  url: "https://hojin.ai/pricing",
                },
                {
                  "@type": "Offer",
                  name: "Pro",
                  price: "29.99",
                  priceCurrency: "EUR",
                  url: "https://hojin.ai/pricing",
                },
              ],
            },
            {
              "@type": "FAQPage",
              "@id": "https://hojin.ai/#faq",
              isPartOf: { "@id": "https://hojin.ai/#webpage" },
              inLanguage: "fr-FR",
              mainEntity: FAQ_ITEMS.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: LandingPage,
});

type Portrait = {
  prenom: string;
  fonction: string;
  modele: string;
  maison: string;
  src: string;
};

const SEPT: Portrait[] = [
  { prenom: "Delphine", fonction: "La Stratège",            modele: "Claude Sonnet 4.6 / Opus 4.8", maison: "Anthropic",  src: pDelphine },
  { prenom: "Henri",    fonction: "Le Financier",           modele: "GPT-5.4 mini / GPT-5.5",       maison: "OpenAI",     src: pHenri },
  { prenom: "Eva",      fonction: "La Communicante",        modele: "Mistral Small / Large",        maison: "Mistral AI", src: pEva },
  { prenom: "Akilah",   fonction: "L'Analyste de marché",   modele: "Gemini 3.5 Flash",             maison: "Google",     src: pAkilah },
  { prenom: "Julien",   fonction: "Le Pragmatique",         modele: "Sonar / Sonar Pro",            maison: "Perplexity", src: pJulien },
  { prenom: "Ren",      fonction: "L'Avocat du Diable",     modele: "DeepSeek V4 Flash",            maison: "DeepSeek",   src: pRen },
  { prenom: "Imani",    fonction: "La Voix humaine",        modele: "Command A",                    maison: "Cohere",     src: pImani },
];

const INVITE: Portrait = {
  prenom: "Pazuru Daiki Fujiwara",
  fonction: "L'Invité",
  modele: "Si vous apportez un document",
  maison: "",
  src: pPazuru,
};

const ACTES = [
  { num: "I",   titre: "L'Antichambre",   txt: "Hōjin vous reçoit. Trois questions affûtent votre intention." },
  { num: "II",  titre: "Le Tour de table", txt: "Sept voix. Sept lectures. Aucun ne se censure." },
  { num: "III", titre: "La Confrontation", txt: "Les Sept se contestent. Ren attaque le consensus." },
  { num: "IV",  titre: "Le Verdict",       txt: "Le Conseil tranche. Vous présidez. Vous décidez." },
];

// Tableau « Une IA seule vs Hōjin » — cœur du positionnement.
// Les lignes marquées { dissent: true } sont des points de tension : accent rouge autorisé.
const TABLE: Array<{ label: string; solo: string; nanae: string; dissent?: boolean }> = [
  { label: "Posture par défaut",  solo: "Vous valider",        nanae: "Vous défier" },
  { label: "Anti-hallucination",  solo: "Aucune garde",        nanae: "Contradiction entre cadres + affirmations non vérifiées marquées « à vérifier »", dissent: true },
  { label: "Ancrage temps réel",  solo: "Connaissances figées", nanae: "Recherche web/réseaux en direct + passe de risques externes (géo, marché, réglementaire)" },
  { label: "Sources citées",      solo: "Variable",            nanae: "Sources multiples, scorées par pertinence" },
  { label: "Contradictions",      solo: "Rarement",            nanae: "Documentées" },
  { label: "Avocat du Diable",    solo: "Aucun",               nanae: "Ren", dissent: true },
  { label: "Note de dissidence",  solo: "Aucune",              nanae: "Systématique", dissent: true },
  { label: "Indice de confiance", solo: "Aucun",               nanae: "Sourcé" },
];

const USAGES = [
  { titre: "Business",  txt: "Décisions stratégiques d'entreprise — arbitrages, priorités, allocation des ressources." },
  { titre: "Tendances", txt: "Lecture des signaux marché et culturels — ce qui monte, ce qui s'effondre, ce qui se déplace." },
  { titre: "Positionnement", txt: "Diagnostiquer après coup une marque, un positionnement — sept regards qui cherchent l'angle mort." },
  { titre: "Audit",     txt: "Le cockpit du dirigeant face à ses conseils — avocat, expert-comptable, banquier. Hōjin rend des observations neutres et les questions à poser à votre professionnel, pour mieux préparer et comprendre vos échanges." },
];

const POURQUOI = [
  {
    titre: "Anti-hallucination par construction",
    txt: "Les sept cadres se contredisent — l'erreur de l'un est rattrapée par les autres. Chaque source est affichée avec un score de pertinence. Toute affirmation non vérifiée est marquée « à vérifier ». Une IA qui dit quand elle ne sait pas.",
  },
  {
    titre: "Le présent, pas seulement la mémoire",
    txt: "Recherche en direct sur le web et les réseaux — Pinterest, Instagram, TikTok, YouTube, X, LinkedIn, travaux universitaires — augmentée d'une passe de risques externes du moment : géopolitiques, réglementaires, marché.",
  },
  {
    titre: "Ancré sur les sources officielles, pas sur la mémoire d'un modèle",
    txt: "Là où une IA seule répond de mémoire, le Conseil consulte les sources de référence françaises pour ancrer ses observations : Légifrance (textes et codes en vigueur), Judilibre (jurisprudence des cours françaises), Pappers (données légales et financières officielles des entreprises). Il nourrit le débat avec ces sources — il ne rend jamais d'avis juridique et ne se substitue pas à un professionnel.",
  },
  {
    titre: "Un verdict qui tranche, traçable jusqu'à la source",
    txt: "Là où une IA seule produit un texte lisse et sans engagement, le Conseil rend un verdict net, assorti d'un indice de conviction et des dissidences assumées — vous voyez qui n'était pas d'accord, et pourquoi. Chaque observation reste reliée à la source qui l'étaye. Vous ne recevez pas une opinion de plus, mais une position argumentée que vous pouvez contester.",
  },
];

const ANCRAGE = [
  {
    titre: "Anti-hallucination par construction",
    txt: "Les sept cadres se contredisent — l'erreur de l'un est rattrapée par les autres. Chaque source affichée avec un score de pertinence. Toute affirmation non vérifiée marquée « à vérifier ». Une IA qui dit quand elle ne sait pas.",
  },
  {
    titre: "Le présent, pas seulement la mémoire",
    txt: "Recherche en direct sur le web et les réseaux — Pinterest, Instagram, TikTok, YouTube, X, LinkedIn, travaux universitaires — augmentée d'une passe de risques externes du moment : géopolitiques, réglementaires, marché.",
  },
  {
    titre: "Ancré sur les sources officielles, pas sur la mémoire d'un modèle",
    txt: "Là où une IA seule répond de mémoire, le Conseil consulte les sources de référence françaises pour ancrer ses observations : Légifrance (textes et codes en vigueur), Judilibre (jurisprudence des cours françaises), Pappers (données légales et financières officielles des entreprises). Le Conseil consulte ces sources pour ancrer ses observations et nourrir le débat — il ne rend jamais d'avis juridique ni ne se substitue à un professionnel.",
  },
];

/* ===================== Citation ===================== */
function Quote({ text, source, variant }: { text: string; source: string; variant?: "plain" }) {
  return (
    <figure className={`qv3-citation${variant === "plain" ? " qv3-citation--plain" : ""}`} data-reveal>
      <blockquote>« {text} »</blockquote>
      <figcaption>{source}</figcaption>
    </figure>
  );
}

/* ===================== Scroll reveal ===================== */
function useReveal() {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return rootRef;
}

/* ===================== FAQ item (a11y) ===================== */
function FaqItem({ q, a, defaultOpen, index }: { q: string; a: string; defaultOpen?: boolean; index: number }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const panelId = `qv3-faq-panel-${index}`;
  const btnId = `qv3-faq-btn-${index}`;

  return (
    <div className={`qv3-faq-item is-in${open ? " is-open" : ""}`} data-reveal>
      <h3 className="qv3-faq-h">
        <button
          id={btnId}
          type="button"
          className="qv3-faq-q"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
        >
          <span>{q}</span>
          <span aria-hidden="true" className="qv3-faq-chevron">+</span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={btnId}
        hidden={!open}
        className="qv3-faq-a"
      >
        <p>{a}</p>
      </div>
    </div>
  );
}


/* ===================== Page ===================== */
export function LandingPage() {
  useDevBypassRedirect();
  const ref = useReveal();

  return (
    <div ref={ref} className="qv3-root">
      <style>{CSS}</style>

      {/* TOPBAR */}
      <header className="qv3-top">
        <div className="qv3-top-inner">
          <a href="/" className="qv3-mark">
            <NanaeLogoFull layout="horizontal" symbolSize="md" wordmarkSize="md" withKanji={false} />
          </a>
          <nav className="qv3-nav" aria-label="Navigation principale">
            <a href="#sept">Les Sept</a>
            <a href="#usages">Usages</a>
            <a href="#manifeste">Manifeste</a>
            <a href="#ancrage">Ancrage</a>
            <a href="#methode">Méthode</a>
            <a href="#memo">Mémo</a>
            <a href="https://conkete.com" rel="noreferrer">Conkete</a>
          </nav>
          <Link to="/signup" className="qv3-nav-cta" aria-label="Convoquer le Conseil Hōjin — créer un compte">Convoquer</Link>
        </div>
      </header>

      {/* ===== SECTION 1 — HERO ===== */}
      <section className="qv3-hero" aria-label="Hero">
        <div className="qv3-hero-inner">
          <p className="qv3-eyebrow" data-reveal>— Vérification post-IA.</p>
          <div className="qv3-hero-logo" data-reveal>
            <NanaeLogoFull layout="vertical" symbolSize="xl" wordmarkSize="xl" />
          </div>

          <h1 className="qv3-tunnel-h1" data-reveal>
            L'IA vous a répondu.<br />
            <em>Mais qui la <span className="fluo-u-blue">vérifie</span> ?</em>
          </h1>
          <p className="qv3-hero-accroche" data-reveal>
            La <em>certitude</em> avant d'agir.
          </p>
          <p className="qv3-tunnel-tag" data-reveal>
            Une IA parle. Sept l'interrogent.
          </p>

          <ul className="qv3-hero-llms" data-reveal aria-label="Les sept intelligences artificielles">
            <li title="Anthropic Claude"><AnthropicLogo /><span className="qv3-hero-llm-name">Claude</span></li>
            <li title="OpenAI GPT"><OpenAILogo /><span className="qv3-hero-llm-name">GPT</span></li>
            <li title="Google Gemini"><GeminiLogo /><span className="qv3-hero-llm-name">Gemini</span></li>
            <li title="Mistral"><MistralLogo /><span className="qv3-hero-llm-name">Mistral</span></li>
            <li title="DeepSeek"><DeepSeekLogo /><span className="qv3-hero-llm-name">DeepSeek</span></li>
            <li title="Perplexity"><PerplexityLogo /><span className="qv3-hero-llm-name">Perplexity</span></li>
            <li title="Cohere"><CohereLogo /><span className="qv3-hero-llm-name">Cohere</span></li>
          </ul>
          <div className="qv3-hero-ctas" data-reveal>
            <Link to="/signup" className="qv3-btn-fuchsia" onClick={() => track("CTA principal", { lieu: "hero" })}>Demander au Conseil</Link>
            <a href="#methode-tunnel" className="qv3-link">Découvrir la méthode</a>
          </div>

        </div>
      </section>

      {/* ===== 2 · LE DOUTE — noir, oppressant ===== */}
      <section className="qv3-tunnel qv3-tunnel-doute" aria-labelledby="tunnel-doute">
        <div className="qv3-wrap qv3-tunnel-wrap">
          <QMono size="xs" caps className="qv3-section-mark" data-reveal>§ Le doute</QMono>
          <h2 id="tunnel-doute" className="qv3-tunnel-h" data-reveal>
            Vous vous êtes fié à une IA.<br />
            <em>Pour ce qui vous engage vraiment.</em>
          </h2>
          <p className="qv3-tunnel-p" data-reveal>
            Un contrat. Des statuts. Une lettre à votre bailleur. Une décision à cinq chiffres. L'IA a répondu en trois secondes, avec l'aplomb de la certitude — <span className="fluo-red">une seule voix</span>, que personne n'a contredite, qui n'a vérifié <span className="fluo-red">aucune source</span>, qui <span className="fluo-red">ignore le droit</span> d'aujourd'hui. Vous avez cliqué « envoyer » avec un doute au fond du ventre.
          </p>
          <p className="qv3-tunnel-verdict-line" data-reveal>
            <span className="fluo-red-strong">Ce doute a raison.</span>
          </p>
        </div>
      </section>

      {/* ===== 3 · LE CAS PHARE — nacré, première respiration ===== */}
      <section className="qv3-tunnel qv3-light qv3-tunnel-cas" aria-labelledby="tunnel-cas">
        <div className="qv3-wrap qv3-tunnel-wrap">
          <QMono size="xs" caps className="qv3-section-mark" data-reveal>§ Le cas phare</QMono>
          <h2 id="tunnel-cas" className="qv3-tunnel-h qv3-tunnel-quote" data-reveal>
            « J'ai fait mon contrat avec ChatGPT.<br />
            <em>Est-ce que je peux le signer ?</em> »
          </h2>
          <p className="qv3-tunnel-p" data-reveal>
            Cette question, des milliers de gens se la posent — seuls, sans savoir à qui la poser. Trop cher de faire rédiger par un avocat. Impossible de vérifier soi-même. Entre les deux : le vide. Hōjin comble ce vide. Il examine votre document, le confronte aux sources officielles — <strong style={{ color: "var(--nacre)", fontWeight: 700 }}>Légifrance, Judilibre</strong>, le droit réellement en vigueur — repère les manques, les incohérences, les angles morts, et dresse la liste précise des points à faire valider. Puis il vous met en relation avec un avocat pour la validation finale. Vous n'arrivez plus démuni. <span className="fluo-green-strong">Vous arrivez armé.</span>
          </p>
        </div>
      </section>

      {/* ===== 4 · LA MÉTHODE — noir, noble ===== */}
      <section id="methode-tunnel" className="qv3-tunnel qv3-tunnel-methode" aria-labelledby="tunnel-methode">
        <div className="qv3-wrap qv3-tunnel-wrap">
          <QMono size="xs" caps className="qv3-section-mark" data-reveal>§ La méthode</QMono>
          <h2 id="tunnel-methode" className="qv3-tunnel-h" data-reveal>
            La rigueur japonaise,<br />
            <em>appliquée à ce qui vous engage.</em>
          </h2>
          <p className="qv3-tunnel-p" data-reveal>
            Le Japon n'a jamais livré ce qu'il n'avait pas éprouvé. Hōjin hérite de cette exigence.
          </p>
          <ul className="qv3-anaphore" aria-label="Trois ruptures avec l'IA seule">
            <li data-reveal>
              <span className="qv3-anaphore-lhs">Là où l'IA répond de mémoire,</span>
              <span className="qv3-anaphore-rhs">Hōjin va chercher dans <span className="fluo-blue">les sources officielles</span> à jour.</span>
            </li>
            <li data-reveal>
              <span className="qv3-anaphore-lhs">Là où l'IA parle d'une seule voix,</span>
              <span className="qv3-anaphore-rhs">sept intelligences s'affrontent — dont une dont l'unique mission est de <span className="fluo-red">démolir le consensus</span>.</span>
            </li>
            <li data-reveal>
              <span className="qv3-anaphore-lhs">Là où l'IA reste figée à hier,</span>
              <span className="qv3-anaphore-rhs">Hōjin est ancré dans aujourd'hui.</span>
            </li>
          </ul>
          <p className="qv3-tunnel-verdict-line" data-reveal>
            Une réponse, ça se lit. <em>Un verdict, ça s'éprouve.</em>
          </p>
        </div>
      </section>

      {/* ===== 5 · LE PROCESS — nacré, séquence claire ===== */}
      <section className="qv3-tunnel qv3-light qv3-tunnel-process" aria-labelledby="tunnel-process">
        <div className="qv3-wrap">
          <header className="qv3-tunnel-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ Le process</QMono>
            <h2 id="tunnel-process" className="qv3-tunnel-h">
              De la réponse au verdict,<br />
              <em>en 7 temps.</em>
            </h2>
          </header>
          <ol className="qv3-process-list qv3-process-flow">
            {[
              { t: "Vous obtenez une réponse d'IA", d: "Un document, une décision, une analyse.", accent: "blue" },
              { t: "Vous la confiez à Hōjin", d: "Un simple copier-coller.", accent: "blue" },
              { t: "Le Conseil enquête", d: "Sources officielles, données à jour, rien de mémorisé.", accent: "blue" },
              { t: "Les sept intelligences débattent", d: "Chaque angle, chaque discipline.", accent: "blue" },
              { t: "Le critique attaque", d: "Il traque la faille que vous n'avez pas vue.", accent: "red" },
              { t: "Le Conseil mesure la certitude", d: "Un score, pas une impression.", accent: "blue" },
              { t: "Hōjin vous arme", d: "Ce qui tient, ce qui manque, les questions à poser — et vers qui vous tourner.", accent: "green" },
            ].map((s, i) => (
              <li key={i} className={`qv3-process-step qv3-process-step--${s.accent}`} data-reveal>
                <span className={`qv3-process-num qv3-process-num--${s.accent}`} aria-hidden>{String(i + 1).padStart(2, "0")}</span>
                <div className="qv3-process-body">
                  <h3 className="qv3-process-t">{s.t}</h3>
                  <p className="qv3-process-d">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ===== 6 · LE RENVERSEMENT — noir, climax ===== */}
      <section className="qv3-tunnel qv3-tunnel-renversement" aria-labelledby="tunnel-renv">
        <div className="qv3-wrap qv3-tunnel-wrap">
          <QMono size="xs" caps className="qv3-section-mark" data-reveal>§ Le renversement</QMono>
          <h2 id="tunnel-renv" className="qv3-tunnel-h qv3-tunnel-h--climax" data-reveal>
            Ne signez plus jamais<br />
            <em>à l'aveugle.</em>
          </h2>
          <p className="qv3-tunnel-p" data-reveal>
            Ce qui vous engage mérite mieux qu'une réponse. Ça mérite <span className="fluo-green">un contre-pouvoir</span>. Hōjin est le vôtre : implacable, <span className="fluo-blue">sourcé</span>, disponible à toute heure.
          </p>
          <p className="qv3-tunnel-verdict-line" data-reveal>
            <span className="fluo-green-strong">Décider en sachant.</span> <em>Signer sans trembler.</em>
          </p>
        </div>
      </section>

      {/* ===== 7 · LES DEUX PORTES — nacré ===== */}
      <section className="qv3-tunnel qv3-light qv3-tunnel-portes" aria-labelledby="tunnel-portes">
        <div className="qv3-wrap">
          <header className="qv3-tunnel-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ Deux portes</QMono>
            <h2 id="tunnel-portes" className="qv3-tunnel-h">
              Deux manières<br />
              <em>de saisir le Conseil.</em>
            </h2>
          </header>
          <div className="qv3-doors">
            <article className="qv3-door qv3-door--blue" data-reveal>
              <div className="qv3-door-num" aria-hidden>I</div>
              <h3 className="qv3-door-t">Éprouvez une réponse d'IA</h3>
              <p className="qv3-door-d">
                Un document, une décision préparée avec l'IA ? Passez-la au Conseil.
              </p>
              <Link to="/signup" className="qv3-link qv3-door-cta">Vérifier une réponse →</Link>
            </article>
            <article className="qv3-door qv3-door--green" data-reveal>
              <div className="qv3-door-num" aria-hidden>II</div>
              <h3 className="qv3-door-t">Consultez directement</h3>
              <p className="qv3-door-d">
                Posez votre question, obtenez d'emblée une réponse déjà éprouvée par la contradiction.
              </p>
              <Link to="/signup" className="qv3-link qv3-door-cta">Saisir le Conseil →</Link>
            </article>
          </div>
        </div>
      </section>

      {/* ===== 8 · CTA FINAL — noir, épuré ===== */}
      <section className="qv3-tunnel qv3-tunnel-final" aria-labelledby="tunnel-final">
        <div className="qv3-wrap qv3-tunnel-wrap">
          <QMono size="xs" caps className="qv3-section-mark" data-reveal>§ Passer à l'acte</QMono>
          <h2 id="tunnel-final" className="qv3-tunnel-h" data-reveal>
            Votre prochaine décision.<br />
            <em>Éprouvée avant d'être prise.</em>
          </h2>
          <div className="qv3-hero-ctas" data-reveal style={{ marginTop: 48 }}>
            <Link to="/signup" className="qv3-btn-fuchsia qv3-btn-lg">Convoquer le Conseil</Link>
          </div>
        </div>
      </section>


      {/* ===== SECTION 2 — LES SEPT ===== */}

      <section id="sept" className="qv3-sept" aria-label="Les Sept intelligences de Hōjin">
        <div className="qv3-wrap">
          <header className="qv3-sec-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ II · Le Conseil</QMono>
            <h2>Les Sept intelligences de Hōjin.</h2>
            <p>Sept écoles, sept biais, sept regards. Le débat est notre méthode.</p>
          </header>

          <div className="qv3-sept-intro" data-reveal>
            <p>
              Chaque jour, un dirigeant prend seul des décisions qui engagent son entreprise. Il a son intuition, peut-être l'avis d'un conseil, mais au fond, un seul point de vue : le sien. Les grandes entreprises, elles, ont un comité, un board, des experts qui se contredisent avant de trancher. Lui, non.
            </p>
            <p>
              ChatGPT ne résout pas ce problème — il donne une réponse lisse, faite pour plaire, depuis une seule intelligence.
            </p>
            <p>
              Hōjin fait autre chose. Il réunit sept modèles d'IA différents — OpenAI, Anthropic, Google, Mistral et d'autres — qui délibèrent et se contredisent réellement, comme un vrai conseil d'administration. L'un d'eux a même pour rôle d'attaquer le consensus. À la fin, vous ne recevez pas une opinion — vous recevez un verdict qui a survécu à la contradiction.
            </p>
          </div>


          <Quote
            variant="plain"
            text="Quand sept intelligences se réunissent, leur clarté égale celle du sage."
            source="Adapté du proverbe japonais 三人寄れば文殊の知恵"
          />

          <ul className="qv3-grid">
            {SEPT.map((p) => (
              <li key={p.prenom} className="qv3-card" data-reveal>
                <div className="qv3-photo">
                  <img src={p.src} alt={`Portrait de ${p.prenom}, ${p.fonction} du Conseil Hōjin`} loading="lazy" width={768} height={1024} />
                </div>
                <h3 className="qv3-card-name">{p.prenom}</h3>
                <p className="qv3-card-role">{p.fonction}</p>
                <span className="qv3-card-maison">{p.maison}</span>
              </li>
            ))}
            <li className="qv3-card qv3-card-guest" data-reveal>
              <div className="qv3-photo qv3-photo-guest">
                <img src={INVITE.src} alt={`Silhouette de ${INVITE.prenom}, ${INVITE.fonction}`} loading="lazy" width={768} height={1024} />
                <span className="qv3-guest-badge" aria-label="Siège d'invité, hors Conseil permanent">Invité</span>
              </div>
              <h3 className="qv3-card-name">{INVITE.prenom}</h3>
              <p className="qv3-card-role">{INVITE.fonction}</p>
              <p className="qv3-card-meta">{INVITE.modele}</p>
            </li>
          </ul>
        </div>
      </section>

      {/* ===== SECTION 2bis — LES USAGES ===== */}
      <section id="usages" className="qv3-usages qv3-light" aria-label="Les usages de Hōjin">
        <div className="qv3-wrap">
          <header className="qv3-sec-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ II.b · Usages</QMono>
            <h2>Quatre branches. Un seul Conseil.</h2>
            <p>Là où une décision se prépare, Hōjin délibère.</p>
          </header>
          <ul className="qv3-usages-grid">
            {USAGES.map((u) => (
              <li key={u.titre} className="qv3-usage" data-reveal>
                <h3 className="qv3-usage-titre">{u.titre}</h3>
                <p className="qv3-usage-txt">{u.txt}</p>
              </li>
            ))}
          </ul>
          <p className="qv3-usages-note" data-reveal>
            <em>Audit</em> prépare et clarifie vos échanges avec vos professionnels — Hōjin ne remplace ni n'évalue un avocat, un expert-comptable, un banquier.
          </p>
        </div>
      </section>

      {/* ===== SECTION 2bis.5 — AFFÛTEZ VOTRE QUESTION ===== */}
      <section id="affutez" className="qv3-affutez" aria-label="Avant de délibérer, on affûte votre question">
        <div className="qv3-wrap">
          <div className="qv3-affutez-inner" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ II.b · Antichambre</QMono>
            <h2 className="qv3-affutez-titre">Avant de délibérer, on affûte votre question.</h2>
            <p className="qv3-affutez-txt">
              La qualité d'un verdict dépend de la qualité de la question. Avant de saisir le Conseil, Hōjin vous pose quelques questions ciblées pour clarifier votre situation, vos contraintes et ce que vous cherchez vraiment à décider. Vous n'avez pas besoin de savoir formuler la demande parfaite — on la construit avec vous, puis les sept cadres délibèrent sur une question nette.
            </p>
            <ul className="qv3-affutez-tags" aria-hidden="true">
              <li>Clarifier</li>
              <li>Cadrer</li>
              <li>Décider</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== SECTION 2ter — POURQUOI LE CONSEIL ===== */}
      <section id="pourquoi" className="qv3-pourquoi-conseil" aria-label="Pourquoi le Conseil">
        <div className="qv3-wrap">
          <header className="qv3-sec-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ II.c · Pourquoi le Conseil</QMono>
            <h2>Pourquoi le Conseil.</h2>
          </header>
          <ol className="qv3-pourquoi-grid">
            {POURQUOI.map((p, i) => (
              <li key={p.titre} className="qv3-pourquoi-item" data-reveal>
                <span className="qv3-pourquoi-num">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="qv3-pourquoi-titre">{p.titre}</h3>
                <p className="qv3-pourquoi-txt">{p.txt}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ===== SECTION 3 — MANIFESTE ===== */}
      <section id="manifeste" className="qv3-manifeste" aria-label="Manifeste">
        <div className="qv3-wrap">
          <header className="qv3-manifeste-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ III · Manifeste</QMono>
            <h2 className="qv3-manifeste-title">Manifeste.</h2>
          </header>

          <div className="qv3-pourquoi" data-reveal>
            <div className="qv3-pourquoi-mark">
              <QMono size="xs" caps>Pourquoi Hōjin</QMono>
            </div>

            <blockquote className="qv3-pourquoi-quote">
              <p>
                « Les assemblées délibérantes seront largement établies,
                et toutes les affaires tranchées par la discussion publique. »
              </p>
              <footer>— Serment en cinq articles, Japon, 1868</footer>
            </blockquote>

            <div className="qv3-pourquoi-prose">
              <p>
                Voici ce que de jeunes samouraïs, artisans de la Restauration,
                rédigèrent en 1868 pour un empereur de quinze ans. Ce serment
                en cinq articles fondera le Japon moderne — et son tout premier
                article érige la <em>délibération</em> en principe impérial.
              </p>
              <p>
                Ce principe n'était pas neuf. Douze siècles plus tôt, en 604,
                le prince Shōtoku l'avait déjà gravé dans la première
                constitution japonaise&nbsp;: les affaires importantes ne se
                tranchent pas par un seul, mais se débattent <em>avec le plus
                grand nombre</em>.
              </p>
              <p className="qv3-pourquoi-pivot">
                Mille deux cent soixante-quatre ans séparent le prince et
                l'empereur. Une même conviction les relie&nbsp;:
                <br /><em>on ne décide pas seul.</em>
              </p>
              <p>
                Hōjin en est l'héritière. Sept intelligences, un Conseil,
                une délibération — pour que vos décisions naissent du débat,
                non de la solitude.
              </p>
            </div>

            <p className="qv3-pourquoi-coda">
              De la confusion <span aria-hidden="true">·</span> à la <em>clarté</em>.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3bis — ANCRAGE DANS LE RÉEL ===== */}
      <section id="ancrage" className="qv3-ancrage qv3-light" aria-label="Comment le Conseil ancre ses réponses dans le réel">
        <div className="qv3-wrap">
          <header className="qv3-sec-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ III.b · Ancrage</QMono>
            <h2>Comment le Conseil ancre ses réponses dans le réel.</h2>
            <p>Trois piliers, une même exigence : que ce qui est dit puisse être vérifié.</p>
          </header>
          <Quote
            text="Connais ce que tu sais, et reconnais ce que tu ignores. C'est cela, savoir."
            source="Confucius · Entretiens"
          />
          <ol className="qv3-ancrage-grid">
            {ANCRAGE.map((a, i) => (
              <li key={a.titre} className="qv3-ancrage-item" data-reveal>
                <span className="qv3-ancrage-num">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="qv3-ancrage-titre">{a.titre}</h3>
                <p className="qv3-ancrage-txt">{a.txt}</p>
              </li>
            ))}
          </ol>

          <div className="qv3-sources-table" data-reveal>
            <div className="qv3-sources-head">
              <QMono size="xs" caps className="qv3-section-mark">Largeur du sourcing</QMono>
              <h3>Officiel · Web · Réseaux · Académique</h3>
            </div>
            <div className="qv3-sources-grid">
              <div className="qv3-sources-col">
                <span className="qv3-sources-cat">Officiel</span>
                <ul>
                  <li>Légifrance <em>— droit en vigueur</em></li>
                  <li>Judilibre <em>— jurisprudence</em></li>
                  <li>Pappers <em>— données entreprises</em></li>
                </ul>
              </div>
              <div className="qv3-sources-col">
                <span className="qv3-sources-cat">Web</span>
                <ul>
                  <li>Presse <em>— FR / international</em></li>
                  <li>Sites officiels <em>— institutions, marques</em></li>
                  <li>Rapports sectoriels <em>— marché</em></li>
                </ul>
              </div>
              <div className="qv3-sources-col">
                <span className="qv3-sources-cat">Réseaux</span>
                <ul>
                  <li>YouTube · TikTok · Instagram</li>
                  <li>X · LinkedIn</li>
                  <li>Pinterest <em>— signaux tendance</em></li>
                </ul>
              </div>
              <div className="qv3-sources-col">
                <span className="qv3-sources-cat">Académique</span>
                <ul>
                  <li>Google Scholar</li>
                  <li>arXiv · SSRN</li>
                  <li>Travaux universitaires</li>
                </ul>
              </div>
            </div>
            <p className="qv3-sources-foot">
              Chaque source citée dans le mémo, avec score de pertinence. Le Conseil consulte ces sources pour ancrer ses observations — il ne rend jamais d'avis juridique.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4 — MÉTHODE ===== */}
      <section id="methode" className="qv3-methode" aria-label="Méthode">
        <div className="qv3-wrap">
          <header className="qv3-sec-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ IV · Méthode</QMono>
            <h2>Quatre actes. Une décision tenue.</h2>
          </header>
          <Quote
            text="Connaître l'autre et se connaître. En cent combats, point de péril."
            source="Sun Tzu · L'Art de la Guerre, Chapitre III"
          />
          <ol className="qv3-actes">
            {ACTES.map((a) => (
              <li key={a.num} className="qv3-acte" data-reveal>
                <span className="qv3-acte-num">{a.num}</span>
                <h3 className="qv3-acte-titre">{a.titre}</h3>
                <p className="qv3-acte-txt">{a.txt}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ===== SECTION 5 — DIFFÉRENCE ===== */}
      <section className="qv3-diff qv3-light" aria-label="La différence">
        <div className="qv3-wrap">
          <header className="qv3-sec-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ V · Différence</QMono>
            <h2>Une IA seule. Et puis Hōjin.</h2>
          </header>
          <Quote
            text="Perçois ce que l'œil ne peut voir."
            source="Miyamoto Musashi · Livre des Cinq Anneaux"
          />
          <div className="qv3-table-wrap" data-reveal>
            <table className="qv3-table">
              <colgroup>
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th aria-hidden="true"></th>
                  <th>Une IA seule</th>
                  <th>Hōjin</th>
                </tr>
              </thead>
              <tbody>
                {TABLE.map((row) => (
                  <tr key={row.label} className={row.dissent ? "is-dissent" : undefined}>
                    <th scope="row">{row.label}</th>
                    <td className="qv3-td-mute"><QMono size="sm">{row.solo}</QMono></td>
                    <td className="qv3-td-strong"><QMono size="sm">{row.nanae}</QMono></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ===== SECTION 6 — MÉMO ===== */}
      <section id="memo" className="qv3-memo" aria-label="Le Mémo">
        <div className="qv3-memo-grid">
          <figure className="qv3-memo-fig" data-reveal>
            <img src={memoImg} alt="Mémo manuscrit sur un bureau sombre, lampe en laiton" loading="lazy" width={1280} height={896} />
          </figure>
          <div className="qv3-memo-text" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ VI · Mémo</QMono>
            <h2>Le Mémo du Conseil.</h2>
            <p>Un document unique. Sept signatures. Toutes les voix.</p>

            <ul className="qv3-memo-feats">
              <li>
                <strong>Fil d'activité en direct</strong> — vous voyez la recherche se faire&nbsp;:
                sources consultées, vérifications, confrontations entre cadres.
              </li>
              <li>
                <strong>Dialogue par surlignage</strong> — surlignez un passage du mémo,
                le Conseil l'approfondit séance tenante.
              </li>
              <li>
                <strong>Mémo téléchargeable (PDF)</strong> — verdict, note de dissidence,
                sources scorées, historique complet du débat.
              </li>
            </ul>

            <p className="qv3-memo-coda"><em>Un mémo qu'on transmet. Qu'on signe. Qu'on défend.</em></p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 7 — APPEL ===== */}
      <section className="qv3-appel qv3-light" aria-label="Saisir le Conseil">
        <div className="qv3-appel-inner">
          <QMono size="xs" caps className="qv3-section-mark" data-reveal>§ VII · Saisine</QMono>
          <h2 data-reveal>Saisissez le Conseil.</h2>
          <p data-reveal>Sept intelligences. Une clarté.</p>
          <Quote
            text="Au moment de la décision, ne tarde pas."
            source="Yamamoto Tsunetomo · Hagakure"
          />
          <div data-reveal>
            <Link to="/signup" className="qv3-btn-gold qv3-btn-lg">Convoquer le Conseil</Link>
          </div>
          <p className="qv3-appel-fine" data-reveal>Première saisine offerte.</p>
        </div>
      </section>

      {/* ===== SECTION 8 — TARIFS ===== */}
      <section id="tarifs" className="qv3-tarifs" aria-label="Tarifs Hōjin">
        <div className="qv3-wrap">
          <header className="qv3-sec-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ VIII · Tarifs</QMono>
            <h2>Nos formules.</h2>
            <p>Une première délibération offerte. Ensuite, la formule qui vous ressemble.</p>
          </header>

          <ul className="qv3-tarifs-grid">
            <li className="qv3-tarif" data-reveal>
              <h3 className="qv3-tarif-nom">Gratuit</h3>
              <p className="qv3-tarif-prix">0€</p>
              <p className="qv3-tarif-inclus">1 délibération offerte</p>
              <Link to="/signup" className="qv3-btn-gold qv3-tarif-cta">Commencer</Link>
            </li>

            <li className="qv3-tarif" data-reveal>
              <h3 className="qv3-tarif-nom">Découverte</h3>
              <p className="qv3-tarif-prix">9,99€<span className="qv3-tarif-per">/mois</span></p>
              <p className="qv3-tarif-inclus">5 délibérations / mois</p>
              <button type="button" disabled aria-disabled="true" className="qv3-tarif-cta qv3-tarif-cta-soon">Bientôt disponible</button>
            </li>

            <li className="qv3-tarif qv3-tarif-highlight" data-reveal>
              <span className="qv3-tarif-badge">Recommandé</span>
              <h3 className="qv3-tarif-nom">Essentiel</h3>
              <p className="qv3-tarif-prix">19,99€<span className="qv3-tarif-per">/mois</span></p>
              <p className="qv3-tarif-inclus">12 délibérations / mois</p>
              <button type="button" disabled aria-disabled="true" className="qv3-tarif-cta qv3-tarif-cta-soon">Bientôt disponible</button>
            </li>

            <li className="qv3-tarif" data-reveal>
              <h3 className="qv3-tarif-nom">Pro</h3>
              <p className="qv3-tarif-prix">29,99€<span className="qv3-tarif-per">/mois</span></p>
              <p className="qv3-tarif-inclus">20 délibérations / mois</p>
              <button type="button" disabled aria-disabled="true" className="qv3-tarif-cta qv3-tarif-cta-soon">Bientôt disponible</button>
            </li>

            <li className="qv3-tarif" data-reveal>
              <h3 className="qv3-tarif-nom">À la carte</h3>
              <p className="qv3-tarif-prix">2,99€<span className="qv3-tarif-per">/délibération</span></p>
              <p className="qv3-tarif-inclus">Sans abonnement</p>
              <Link to="/pricing" className="qv3-btn-gold qv3-tarif-cta">Acheter à l'unité</Link>
            </li>
          </ul>
        </div>
      </section>

      {/* ===== SECTION 9 — FAQ ===== */}
      <section id="faq" className="qv3-faq" aria-label="Questions fréquentes">
        <div className="qv3-wrap">
          <header className="qv3-sec-head" data-reveal>
            <QMono size="xs" caps className="qv3-section-mark">§ IX · FAQ</QMono>
            <h2>Questions fréquentes.</h2>
          </header>

          <div className="qv3-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem key={item.q} q={item.q} a={item.a} defaultOpen={i === 0} index={i} />
            ))}

          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="qv3-foot">
        <div className="qv3-foot-inner">
          <div className="qv3-foot-mark">
            <NanaeLogoSymbol size="md" />
            <NanaeWordmark size="md" />
          </div>
          <div className="qv3-foot-center">
            <QMono size="xs" caps className="qv3-foot-tag">Sept voix, une direction</QMono>
            <p className="qv3-foot-quote"><em>Conçu dans la philosophie japonaise.</em></p>
          </div>
          <nav className="qv3-foot-nav" aria-label="Pied de page">
            <a href="#manifeste">Manifeste</a>
            <a href="#methode">Méthode</a>
            <a href="https://conkete.com" rel="noreferrer">Conkete</a>
            <Link to="/legal">Mentions légales</Link>
          </nav>
        </div>
        <div className="qv3-foot-sig">
          <span className="qv3-foot-kanji" aria-hidden="true">法人</span>
          <span>hojin.ai · MMXXVI</span>
        </div>
      </footer>
    </div>
  );
}

/* ===================== CSS — palette noir / nacré / rouge en pointes ===================== */
const RED = "#FF1744";       // accent rare — CTA, tension, dissidence
const NACRE = "#FAFAFA";     // texte principal — blanc lumineux légèrement nacré
const CSS = `
.qv3-root {
  --ink: #000000;            /* fond noir partout */
  --nacre: ${NACRE};         /* texte principal */
  --red: ${RED};             /* accent en pointes — usage parcimonieux */
  --grey-300: #D8D8D8;       /* secondaire clair — WCAG AAA sur noir */
  --grey-500: #A6A6A6;       /* tertiaire — WCAG AA sur noir (5.5:1) */
  --grey-700: #2A2A2A;
  --hairline: rgba(250,250,250, 0.14);
  background: var(--ink);
  color: var(--nacre);
  font-family: "Inter", "Geist", system-ui, -apple-system, sans-serif;
  font-feature-settings: "ss01", "cv01";
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  letter-spacing: -0.005em;
}
.qv3-root * { box-sizing: border-box; }
.qv3-root img { display: block; max-width: 100%; height: auto; }

/* reveal */
.qv3-root [data-reveal] {
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 900ms cubic-bezier(.2,.7,.2,1), transform 900ms cubic-bezier(.2,.7,.2,1);
  will-change: opacity, transform;
}
.qv3-root [data-reveal].is-in { opacity: 1; transform: none; }
/* LCP : le hero est au-dessus de la ligne de flottaison — jamais masqué au
   premier rendu (sinon le titre n'apparaît qu'après hydratation + IO). */
.qv3-root .qv3-hero [data-reveal] { opacity: 1; transform: none; }
@media (prefers-reduced-motion: reduce) {
  .qv3-root [data-reveal] { opacity: 1; transform: none; transition: none; }
}


/* section marks (industrial Sony eyebrow) */
.qv3-section-mark {
  display: inline-block;
  color: var(--grey-500);
  margin-bottom: 24px;
  letter-spacing: 0.24em !important;
}

/* layout */
.qv3-wrap { width: 100%; max-width: 1200px; margin: 0 auto; padding: 0 28px; }
@media (min-width: 768px) { .qv3-wrap { padding: 0 48px; } }

/* topbar */
.qv3-top {
  position: fixed; top: 0; left: 0; right: 0; z-index: 50;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid var(--hairline);
}
.qv3-top-inner {
  max-width: 1400px; margin: 0 auto;
  padding: 12px 24px;
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
}
.qv3-mark { text-decoration: none; display: inline-flex; align-items: center; color: #FFFFFF; }
.qv3-mark .nanae-logo-full,
.qv3-mark .nanae-wordmark { color: #FFFFFF; }
.qv3-nav { display: none; gap: 28px; }
.qv3-nav a {
  color: rgba(245,239,227,0.72); text-decoration: none; font-size: 13px;
  letter-spacing: 0.04em; transition: color 200ms;
}
.qv3-nav a:hover { color: var(--nacre); }
.qv3-nav-cta {
  font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--nacre); text-decoration: none;
  border: 1px solid var(--red); padding: 9px 16px;
  transition: background 200ms, color 200ms;
}
.qv3-nav-cta:hover { background: var(--red); color: var(--nacre); }
@media (min-width: 900px) { .qv3-nav { display: flex; } }

/* HERO */
.qv3-hero {
  background: var(--ink); color: var(--nacre);
  min-height: 100vh;
  display: flex; align-items: center; justify-content: center;
  padding: 120px 24px 80px;
  position: relative;
}
.qv3-hero-inner { text-align: center; max-width: 760px; }
.qv3-eyebrow {
  font-size: 12px; letter-spacing: 0.28em; text-transform: uppercase;
  color: var(--grey-500); margin: 0 0 48px;
}
.qv3-hero-logo {
  display: flex; justify-content: center; align-items: center;
  margin: 0 auto;
  color: var(--nacre);
}
.qv3-hero-logo .nanae-logo-full,
.qv3-hero-logo .nanae-wordmark { color: var(--nacre); }
.qv3-hero-sub {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-weight: 300; font-style: italic;
  font-size: clamp(20px, 2.6vw, 28px);
  line-height: 1.4;
  margin: 40px auto 0; max-width: 560px;
  color: var(--nacre);
}
.qv3-hero-llms {
  list-style: none; padding: 0; margin: 32px auto 0;
  display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: center;
  gap: clamp(20px, 4vw, 44px);
  max-width: 720px;
  color: rgba(245,239,227,0.72);
}
.qv3-hero-llms li {
  display: inline-flex; flex-direction: column; align-items: center; justify-content: flex-start;
  gap: 8px;
  transition: color 200ms ease, opacity 200ms ease;
  opacity: 0.85;
}
.qv3-hero-llms li:hover { color: var(--nacre); opacity: 1; }
.qv3-hero-llms svg { height: 26px; width: 26px; display: block; flex-shrink: 0; }
.qv3-hero-llm-name {
  font-family: "DM Mono", ui-monospace, monospace;
  font-size: 10.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #A6A6A6;
  white-space: nowrap;
}

.qv3-hero-accroche {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-weight: 300;
  font-size: clamp(20px, 2.2vw, 26px);
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: var(--nacre);
  margin: 18px auto 14px;
  max-width: 640px;
}
.qv3-hero-accroche em {
  font-style: italic;
  color: var(--nacre);
  border-bottom: 1px solid rgba(0, 229, 255, 0.55);
  padding-bottom: 2px;
}
.qv3-hero-ctas {
  margin-top: 64px;
  display: flex; flex-direction: column; align-items: center; gap: 22px;
}
@media (min-width: 640px) {
  .qv3-hero-ctas { flex-direction: row; justify-content: center; gap: 36px; }
}

/* boutons (CTA = pointe rouge) */
.qv3-btn-gold {
  display: inline-block;
  font-family: "Playfair Display", Georgia, serif;
  font-size: 14px; letter-spacing: 0.08em;
  color: var(--nacre); text-decoration: none;
  padding: 16px 32px;
  border: 1px solid var(--red);
  background: transparent;
  transition: background 240ms ease, color 240ms ease;
}
.qv3-btn-gold:hover { background: var(--red); color: var(--nacre); }
.qv3-btn-lg { padding: 20px 44px; font-size: 15px; letter-spacing: 0.1em; }
.qv3-link {
  font-size: 13px; letter-spacing: 0.06em; color: var(--nacre); text-decoration: none;
  border-bottom: 1px solid rgba(245,239,227,0.4);
  padding-bottom: 3px;
  transition: border-color 200ms;
}
.qv3-link:hover { border-color: var(--red); }

/* section heads */
.qv3-sec-head { text-align: center; max-width: 760px; margin: 0 auto 80px; }
.qv3-sec-head h2 {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-weight: 300; font-size: clamp(36px, 5vw, 60px);
  letter-spacing: -0.03em; line-height: 1.05; margin: 0;
  color: var(--nacre);
}
.qv3-sec-head p {
  margin: 18px 0 0; font-size: 16px; color: var(--grey-300);
  letter-spacing: 0.02em;
}

/* SEPT */
.qv3-sept { background: var(--ink); padding: 140px 0 160px; border-top: 1px solid var(--hairline); }
.qv3-sept-intro {
  max-width: 680px;
  margin: 48px auto 56px;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 20px;
  line-height: 1.6;
  color: var(--ivory);
  opacity: 0.88;
}
.qv3-sept-intro p { margin: 0 0 18px; }
.qv3-sept-intro p:last-child { margin-bottom: 0; }
@media (max-width: 639px) {
  .qv3-sept-intro { font-size: 17px; margin: 32px auto 40px; padding: 0 4px; }
}
.qv3-grid {
  list-style: none; margin: 0; padding: 0;
  display: grid; grid-template-columns: 1fr; gap: 20px;
}
@media (min-width: 640px) { .qv3-grid { grid-template-columns: repeat(2, 1fr); gap: 56px 32px; } }
@media (min-width: 1024px) { .qv3-grid { grid-template-columns: repeat(4, 1fr); } }
.qv3-card { text-align: left; }

/* Mobile : cartes horizontales compactes */
@media (max-width: 639px) {
  .qv3-sept { padding: 80px 0 100px; }
  .qv3-card {
    display: grid;
    grid-template-columns: 88px 1fr;
    gap: 18px;
    align-items: center;
  }
  .qv3-card .qv3-photo {
    aspect-ratio: 1 / 1;
    margin-bottom: 0;
    width: 88px;
    height: 88px;
  }
  .qv3-card-name { font-size: 22px; }
  .qv3-card-maison { font-size: 11px; padding: 4px 9px; }
  .qv3-card-role { margin-top: 4px; font-size: 10px; }
  .qv3-card-meta { margin-top: 6px; font-size: 11px; }
}

.qv3-photo {
  aspect-ratio: 3/4;
  background: #111;
  overflow: hidden;
  margin-bottom: 20px;
  filter: grayscale(100%) contrast(1.05);
}
.qv3-photo img { width: 100%; height: 100%; object-fit: cover; }
.qv3-card-name {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-weight: 400; font-size: 28px; letter-spacing: -0.02em;
  margin: 0; color: var(--nacre);
}
.qv3-card-role {
  margin: 6px 0 0;
  font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--nacre); font-variant: small-caps;
}
.qv3-card-maison {
  display: inline-block;
  margin: 12px 0 0;
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0A0A0A;
  background: #FFFFFF;
  padding: 5px 11px;
  border-radius: 3px;
  box-shadow: 0 4px 16px -8px rgba(255,255,255,0.3), 0 0 0 1px rgba(255,255,255,0.85);
}
.qv3-card-meta {
  margin: 14px 0 0; font-size: 12px;
  color: var(--grey-500); letter-spacing: 0.02em;
}
.qv3-card-guest .qv3-photo { background: #1A1A1A; }
.qv3-card-guest .qv3-card-name,
.qv3-card-guest .qv3-card-role { color: var(--grey-500); }
.qv3-photo-guest { position: relative; }
.qv3-photo-guest img {
  opacity: 0.42;
  filter: grayscale(100%) contrast(0.95) brightness(0.78) blur(1.5px);
  transition: opacity .4s ease, filter .4s ease;
}
.qv3-card-guest:hover .qv3-photo-guest img {
  opacity: 0.6;
  filter: grayscale(100%) contrast(1) brightness(0.88) blur(0.5px);
}
.qv3-guest-badge {
  position: absolute;
  top: 12px; left: 12px;
  padding: 5px 10px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--nacre, #efe7d8);
  background: rgba(10, 10, 10, 0.72);
  border: 1px solid var(--rouge, #b3121f);
  backdrop-filter: blur(4px);
  z-index: 2;
}

/* USAGES */
.qv3-usages { background: var(--ink); padding: 140px 0 160px; border-top: 1px solid var(--hairline); }
.qv3-usages-grid {
  list-style: none; margin: 0; padding: 0;
  display: grid; grid-template-columns: 1fr; gap: 32px;
}
@media (min-width: 720px)  { .qv3-usages-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1100px) { .qv3-usages-grid { grid-template-columns: repeat(4, 1fr); } }
.qv3-usage {
  padding: 28px 24px 32px;
  border-top: 1px solid var(--hairline);
  border-left: 1px solid var(--hairline);
}
.qv3-usage-titre {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 400; font-size: 26px; letter-spacing: -0.01em;
  margin: 0 0 14px; color: var(--nacre);
}
.qv3-usage-txt { margin: 0; font-size: 14.5px; line-height: 1.6; color: var(--grey-300); }
.qv3-usages-note {
  margin: 56px auto 0; max-width: 760px;
  text-align: center;
  font-family: "Playfair Display", Georgia, serif;
  font-style: italic; font-size: 15px; color: var(--grey-300);
}
.qv3-usages-note em { color: var(--nacre); font-style: italic; font-weight: 500; }

/* POURQUOI Hōjin — sous-section du Manifeste */
.qv3-pourquoi {
  max-width: 760px;
  margin: 140px auto 0;
  padding: 0 24px;
  text-align: center;
}
.qv3-pourquoi-mark {
  display: flex; justify-content: center;
  margin-bottom: 28px;
}
.qv3-pourquoi-mark::before,
.qv3-pourquoi-mark::after {
  content: ""; flex: 0 0 56px;
  border-top: 0.5px solid var(--hairline);
  margin: 9px 16px 0;
}
.qv3-pourquoi-quote { margin: 0 0 56px; padding: 0 8px; }
.qv3-pourquoi-quote p {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-style: italic; font-weight: 300;
  font-size: clamp(20px, 2.4vw, 26px);
  line-height: 1.45; letter-spacing: -0.01em;
  color: var(--nacre);
  margin: 0 0 14px;
}
.qv3-pourquoi-quote footer {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 10.5px; letter-spacing: 0.22em;
  text-transform: uppercase; color: var(--grey-500);
}
.qv3-pourquoi-prose { text-align: left; max-width: 640px; margin: 0 auto; }
.qv3-pourquoi-prose p {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 300;
  font-size: 17px;
  line-height: 1.75;
  letter-spacing: -0.005em;
  color: var(--nacre);
  margin: 0 0 22px;
}
.qv3-pourquoi-prose em { color: var(--nacre); font-style: italic; font-weight: 500; }
.qv3-pourquoi-pivot { text-align: center !important; margin: 36px 0 !important; font-size: 18px !important; }
.qv3-pourquoi-coda {
  margin: 56px 0 0;
  font-family: "Playfair Display", Georgia, serif;
  font-style: italic; font-weight: 300;
  font-size: clamp(22px, 2.6vw, 30px);
  letter-spacing: -0.01em;
  color: var(--nacre);
}
.qv3-pourquoi-coda span { color: var(--grey-500); margin: 0 10px; }
.qv3-pourquoi-coda em { color: var(--nacre); font-style: italic; }

/* MANIFESTE */
.qv3-manifeste {
  background: var(--ink);
  padding: 180px 0 200px;
  border-top: 1px solid var(--hairline);
}
.qv3-manifeste-head { text-align: center; margin: 0 auto 64px; }
.qv3-manifeste-title {
  font-family: "Playfair Display", Georgia, serif;
  font-style: italic; font-weight: 300;
  font-size: clamp(40px, 5vw, 64px);
  letter-spacing: -0.03em;
  margin: 0; color: var(--nacre);
}
.qv3-quote { margin: 0 auto; text-align: center; max-width: 900px; padding: 0 24px; }
.qv3-quote p {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-style: italic; font-weight: 300;
  font-size: clamp(28px, 4.4vw, 52px);
  line-height: 1.2; letter-spacing: -0.03em;
  margin: 0; color: var(--nacre);
}
.qv3-quote-2 { margin-top: 36px !important; }
.qv3-quote em { color: var(--nacre); font-style: italic; }
.qv3-quote footer {
  margin-top: 64px; font-size: 11px;
  letter-spacing: 0.24em; text-transform: uppercase;
  color: var(--grey-500); text-align: center;
}

/* ANCRAGE */
.qv3-ancrage { background: var(--ink); padding: 160px 0 180px; border-top: 1px solid var(--hairline); }
.qv3-ancrage-grid {
  list-style: none; margin: 0; padding: 0;
  display: grid; grid-template-columns: 1fr; gap: 56px;
  max-width: 1000px; margin-left: auto; margin-right: auto;
}
@media (min-width: 900px) { .qv3-ancrage-grid { grid-template-columns: 1fr 1fr; gap: 64px; } }
.qv3-ancrage-item { padding-top: 12px; border-top: 1px solid var(--hairline); }
.qv3-ancrage-num {
  display: block;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase;
  color: var(--grey-500); margin-bottom: 20px;
}
.qv3-ancrage-titre {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 400; font-size: clamp(22px, 2.4vw, 28px);
  letter-spacing: -0.01em;
  margin: 0 0 16px; color: var(--nacre);
}
.qv3-ancrage-txt { margin: 0; font-size: 15.5px; line-height: 1.7; color: var(--grey-300); }

/* POURQUOI LE CONSEIL — fond blanc, texte noir, rouge conservé */
.qv3-pourquoi-conseil { background: #ffffff; color: #0a0a0a; padding: 160px 0 180px; border-top: 1px solid rgba(0,0,0,0.08); }
.qv3-pourquoi-conseil .qv3-section-mark { color: rgba(0,0,0,0.55); }
.qv3-pourquoi-conseil .qv3-sec-head h2 { color: #0a0a0a; }
.qv3-pourquoi-conseil .qv3-pourquoi-item { border-top-color: rgba(0,0,0,0.12); }
.qv3-pourquoi-conseil .qv3-pourquoi-titre { color: #0a0a0a; }
.qv3-pourquoi-conseil .qv3-pourquoi-txt { color: #2a2a2a; }
.qv3-pourquoi-grid {
  list-style: none; margin: 0 auto; padding: 0;
  display: grid; grid-template-columns: 1fr; gap: 56px;
  max-width: 1100px;
  align-items: stretch;
}
@media (min-width: 900px) { .qv3-pourquoi-grid { grid-template-columns: 1fr 1fr; gap: 64px; } }
.qv3-pourquoi-item {
  display: flex; flex-direction: column;
  padding: 16px 18px 18px; border-top: 1px solid var(--hairline);
  height: 100%;
  transition: background-color 220ms ease, border-top-color 220ms ease, transform 220ms ease;
}
.qv3-pourquoi-conseil .qv3-pourquoi-item:hover {
  background: rgba(0,0,0,0.035);
  border-top-color: rgba(0,0,0,0.28);
}
@media (max-width: 768px) {
  .qv3-pourquoi-conseil { padding: 72px 0 80px; }
  .qv3-pourquoi-grid { gap: 32px; }
  .qv3-pourquoi-item { padding: 14px 4px 16px; }
  .qv3-pourquoi-titre { font-size: 20px; margin-bottom: 12px; }
  .qv3-pourquoi-num { margin-bottom: 14px; }
  .qv3-pourquoi-txt { font-size: 13.5px; line-height: 1.7; }
}


/* AFFÛTEZ VOTRE QUESTION — respiration courte */
.qv3-affutez { background: var(--ink); padding: 120px 0 100px; border-top: 1px solid var(--hairline); }
.qv3-affutez-inner { max-width: 760px; margin: 0 auto; text-align: center; padding: 0 8px; }
.qv3-affutez-titre {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 400; font-size: clamp(26px, 3.2vw, 36px);
  letter-spacing: -0.01em; line-height: 1.2;
  margin: 18px 0 24px; color: var(--nacre);
}
.qv3-affutez-txt {
  margin: 0 auto;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 14.5px; line-height: 1.8; color: var(--grey-300);
  max-width: 640px;
}
.qv3-affutez-tags {
  list-style: none; margin: 32px 0 0; padding: 0;
  display: flex; gap: 28px; justify-content: center; flex-wrap: wrap;
}
.qv3-affutez-tags li {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase;
  color: #FF6672;
  position: relative; padding-left: 14px;
}
.qv3-affutez-tags li::before {
  content: ""; position: absolute; left: 0; top: 50%;
  width: 6px; height: 6px; transform: translateY(-50%) rotate(45deg);
  background: #FF6672;
}

.qv3-pourquoi-num {
  display: block;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase;
  color: var(--rouge, #c8322a); margin-bottom: 20px;
}
.qv3-pourquoi-titre {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 400; font-size: clamp(22px, 2.4vw, 28px);
  letter-spacing: -0.01em;
  margin: 0 0 16px; color: var(--nacre);
}
.qv3-pourquoi-txt {
  margin: 0;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 14px; line-height: 1.75; color: var(--grey-300);
}

/* SOURCES TABLE */
.qv3-sources-table {
  max-width: 1100px;
  margin: clamp(56px, 9vw, 96px) auto 0;
  padding: clamp(24px, 4vw, 40px) clamp(18px, 3vw, 32px);
  border: 1px solid var(--hairline);
  background: rgba(255,255,255,0.012);
  border-radius: 2px;
}
.qv3-sources-head {
  text-align: center;
  margin-bottom: clamp(28px, 4vw, 40px);
  padding: 0 8px;
}
.qv3-sources-head h3 {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 400;
  font-size: clamp(18px, 2.2vw, 24px);
  line-height: 1.3;
  margin: 12px 0 0;
  color: var(--nacre);
  letter-spacing: -0.01em;
  text-wrap: balance;
}
.qv3-sources-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  align-items: start;
}
@media (min-width: 640px) {
  .qv3-sources-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 36px 32px; }
}
@media (min-width: 980px) {
  .qv3-sources-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 32px 28px; }
}
.qv3-sources-col {
  min-width: 0;
  padding-top: 14px;
  border-top: 1px solid var(--hairline);
  display: flex;
  flex-direction: column;
}
.qv3-sources-cat {
  display: block;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 10.5px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #B3121F;
  margin-bottom: 14px;
}
.qv3-sources-col ul { list-style: none; margin: 0; padding: 0; }
.qv3-sources-col li {
  font-size: 14px;
  line-height: 1.55;
  color: var(--nacre);
  padding: 10px 0;
  border-bottom: 1px dashed var(--hairline);
  word-break: break-word;
  hyphens: auto;
}
.qv3-sources-col li:last-child { border-bottom: none; }
.qv3-sources-col li em {
  display: block;
  font-style: normal;
  color: var(--grey-500);
  font-size: 12px;
  margin-top: 2px;
  letter-spacing: 0.01em;
}
.qv3-sources-foot {
  margin: clamp(24px, 4vw, 32px) auto 0;
  max-width: 720px;
  text-align: center;
  font-size: 12.5px;
  color: var(--grey-500);
  line-height: 1.65;
  padding: 0 8px;
  text-wrap: pretty;
}
@media (max-width: 480px) {
  .qv3-sources-table { margin-left: -4px; margin-right: -4px; }
  .qv3-sources-col li em { display: inline; margin-top: 0; }
}

/* MÉTHODE */
.qv3-methode { background: var(--ink); color: var(--nacre); padding: 160px 0 180px; border-top: 1px solid var(--hairline); }
.qv3-actes {
  list-style: none; margin: 0; padding: 0;
  display: grid; grid-template-columns: 1fr; gap: 56px;
}
@media (min-width: 768px) { .qv3-actes { grid-template-columns: repeat(2, 1fr); gap: 64px 48px; } }
@media (min-width: 1100px) { .qv3-actes { grid-template-columns: repeat(4, 1fr); gap: 48px; } }
.qv3-acte { padding-top: 8px; border-top: 1px solid var(--hairline); }
.qv3-acte-num {
  display: block;
  font-family: "Playfair Display", Georgia, serif;
  font-style: italic; font-weight: 300;
  font-size: 56px; line-height: 1; color: var(--nacre);
  margin-bottom: 24px;
}
.qv3-acte-titre {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 400; font-size: 22px; letter-spacing: -0.01em;
  margin: 0 0 12px; color: var(--nacre);
}
.qv3-acte-txt { margin: 0; font-size: 14px; line-height: 1.55; color: var(--grey-300); }

/* DIFFÉRENCE */
.qv3-diff { background: var(--ink); padding: 160px 0 180px; border-top: 1px solid var(--hairline); }
.qv3-table-wrap { max-width: 920px; margin: 0 auto; }
.qv3-table { width: 100%; border-collapse: collapse; }
.qv3-table th, .qv3-table td {
  text-align: left; padding: 24px 16px;
  border-bottom: 1px solid var(--hairline);
  font-size: 15px;
  vertical-align: top;
}
.qv3-table thead th {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 400; font-size: 18px; letter-spacing: -0.01em;
  color: var(--nacre); padding-bottom: 20px; border-bottom: 1px solid var(--nacre);
}
.qv3-table tbody th {
  font-weight: 400; color: var(--grey-300);
  font-size: 13px; letter-spacing: 0.06em; text-transform: uppercase;
}
.qv3-td-mute { color: var(--grey-500); }
.qv3-td-strong { color: var(--nacre); font-weight: 500; }
.qv3-table .qv3-mono-hud { color: inherit; }
/* lignes de tension : pointe rouge à gauche, accent sur la colonne Hōjin */
.qv3-table tbody tr.is-dissent th { box-shadow: inset 2px 0 0 0 var(--red); padding-left: 18px; }
.qv3-table tbody tr.is-dissent .qv3-td-strong { color: var(--nacre); }
.qv3-table tbody tr.is-dissent .qv3-td-strong::after {
  content: "·"; color: var(--red); margin-left: 6px; font-weight: 700;
}
@media (max-width: 720px) {
  .qv3-table-wrap { max-width: 100%; }
  .qv3-table { table-layout: fixed; }
  .qv3-table th, .qv3-table td {
    padding: 16px 8px; font-size: 13px;
    word-break: break-word; overflow-wrap: anywhere;
  }
  .qv3-table thead th { font-size: 15px; padding-bottom: 14px; }
  .qv3-table tbody th { font-size: 11px; letter-spacing: 0.04em; }
  .qv3-table colgroup col:nth-child(1) { width: 34%; }
  .qv3-table colgroup col:nth-child(2) { width: 30%; }
  .qv3-table colgroup col:nth-child(3) { width: 36%; }
}

/* MÉMO */
.qv3-memo { background: var(--ink); color: var(--nacre); padding: 0; border-top: 1px solid var(--hairline); }
.qv3-memo-grid {
  display: grid; grid-template-columns: 1fr;
  min-height: 100vh;
}
@media (min-width: 900px) { .qv3-memo-grid { grid-template-columns: 1fr 1fr; } }
.qv3-memo-fig { margin: 0; overflow: hidden; min-height: 50vh; }
.qv3-memo-fig img { width: 100%; height: 100%; object-fit: cover; filter: grayscale(100%) contrast(1.05); }
.qv3-memo-text {
  padding: 100px 32px;
  display: flex; flex-direction: column; justify-content: center;
  max-width: 580px;
}
@media (min-width: 900px) { .qv3-memo-text { padding: 120px 64px; } }
.qv3-memo-text h2 {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 300; font-size: clamp(34px, 4.4vw, 52px);
  letter-spacing: -0.03em; margin: 0 0 28px; color: var(--nacre);
}
.qv3-memo-text > p {
  margin: 0 0 24px; font-size: 17px; line-height: 1.6;
  color: var(--grey-300);
}
.qv3-memo-feats {
  list-style: none; margin: 8px 0 24px; padding: 0;
  display: flex; flex-direction: column; gap: 18px;
}
.qv3-memo-feats li {
  padding: 18px 0 0;
  border-top: 1px solid var(--hairline);
  font-size: 15px; line-height: 1.6; color: var(--grey-300);
}
.qv3-memo-feats strong {
  display: block; margin-bottom: 4px;
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 500; font-size: 17px; letter-spacing: -0.005em;
  color: var(--nacre);
}
.qv3-memo-coda {
  margin-top: 28px !important;
  font-family: "Inter", "Geist", system-ui, -apple-system, sans-serif !important;
  font-style: normal !important;
  font-weight: 500 !important;
  font-size: 20px !important;
  line-height: 1.45 !important;
  letter-spacing: -0.005em !important;
  color: var(--nacre) !important;
}
.qv3-memo-coda em { font-style: normal; }

/* APPEL */
.qv3-appel {
  background: var(--ink); color: var(--nacre);
  min-height: 90vh;
  display: flex; align-items: center; justify-content: center;
  padding: 140px 24px;
  border-top: 1px solid var(--hairline);
  text-align: center;
}
.qv3-appel-inner { text-align: center; max-width: 720px; margin: 0 auto; width: 100%; }
.qv3-appel-inner > * { margin-left: auto; margin-right: auto; }
.qv3-appel-inner .qv3-quote { text-align: center; }
.qv3-appel h2 {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 300; font-size: clamp(44px, 6.4vw, 84px);
  letter-spacing: -0.04em; margin: 0; color: var(--nacre);
}
.qv3-appel p {
  margin: 32px auto 56px; max-width: 540px;
  font-family: "Playfair Display", Georgia, serif; font-style: italic;
  font-size: clamp(18px, 2.2vw, 22px); color: var(--grey-300);
  line-height: 1.5;
}
.qv3-appel-fine {
  display: inline-block;
  margin-top: 40px !important;
  padding: 10px 18px;
  background: #B3121F;
  color: #FFFFFF !important;
  font-family: "JetBrains Mono", ui-monospace, monospace !important;
  font-style: normal !important;
  font-weight: 600;
  font-size: 12px !important;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  border-radius: 2px;
  box-shadow: 0 8px 24px -10px color-mix(in oklab, var(--red) 60%, transparent);
}

/* FOOTER */
.qv3-foot { background: var(--ink); color: var(--grey-500); padding: 56px 0 32px; border-top: 1px solid var(--hairline); }
.qv3-foot-inner {
  max-width: 1200px; margin: 0 auto; padding: 0 28px;
  display: flex; flex-direction: column; align-items: center; gap: 28px; text-align: center;
}
@media (min-width: 900px) {
  .qv3-foot-inner { flex-direction: row; justify-content: space-between; align-items: center; text-align: left; }
}
.qv3-foot-mark {
  display: inline-flex; align-items: center; gap: 12px;
  color: var(--nacre);
}
.qv3-foot-mark .nanae-wordmark { color: var(--nacre); }
.qv3-foot-center { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.qv3-foot-tag { color: var(--nacre) !important; letter-spacing: 0.24em !important; }
.qv3-foot-quote { margin: 0; font-size: 13px; color: var(--grey-300); font-family: "Playfair Display", Georgia, serif; }
.qv3-foot-nav { display: flex; gap: 22px; flex-wrap: wrap; justify-content: center; }
.qv3-foot-nav a { color: var(--grey-500); text-decoration: none; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; transition: color 200ms; }
.qv3-foot-nav a:hover { color: var(--nacre); }
.qv3-foot-sig {
  max-width: 1200px; margin: 40px auto 0; padding: 24px 28px 0;
  border-top: 1px solid var(--hairline);
  display: flex; justify-content: space-between; align-items: center;
  font-family: "JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace;
  font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--grey-500);
}
.qv3-foot-kanji {
  font-family: "Yu Mincho", "Hiragino Mincho Pro", "MS Mincho", serif;
  font-size: 14px; letter-spacing: 0.1em; color: var(--nacre);
}

/* CITATIONS — Playfair italic, filets nacrés très fins */
.qv3-citation {
  max-width: 680px;
  margin: 96px auto;
  padding: 32px 24px;
  text-align: center;
  border-top: 0.5px solid var(--hairline);
  border-bottom: 0.5px solid var(--hairline);
}
.qv3-citation blockquote {
  margin: 0;
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(18px, 2vw, 20px);
  line-height: 1.55;
  color: var(--grey-300);
  letter-spacing: -0.005em;
}
.qv3-citation figcaption {
  margin-top: 18px;
  font-family: "JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--grey-500);
}
.qv3-citation--plain blockquote {
  font-family: "DM Sans", "Inter", system-ui, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: clamp(18px, 2vw, 22px);
  line-height: 1.6;
  letter-spacing: 0;
  color: var(--ivory, #F7F2EA);
}

/* ===== LIGHT SECTIONS — alternance nacré / noir ===== */
/* Re-scope les tokens locaux : --ink devient nacré, --nacre devient noir,
   les gris s'inversent, les filets passent en noir transparent.
   Le rouge (var(--red)) reste le fil conducteur. */
.qv3-root .qv3-light {
  --ink: #FFFFFF;            /* blanc nacré pur */
  --nacre: #000000;          /* texte principal en noir */
  --grey-300: #111111;       /* secondaire — noir profond */
  --grey-500: #333333;       /* tertiaire — WCAG AAA */
  --grey-700: #EFEFEF;
  --hairline: rgba(0,0,0, 0.14);
  background: #FFFFFF;
  color: #000000;
  border-top: 1px solid rgba(0,0,0, 0.08);
}
/* Liaison visuelle : un filet rouge fin marque la bascule de palette */
.qv3-root .qv3-light::before {
  content: "";
  display: block;
  width: 56px;
  height: 1px;
  background: var(--red);
  margin: 0 auto;
  transform: translateY(-1px);
}
/* Force les surfaces "blanches" éventuelles à rester nacrées */
.qv3-root .qv3-light .qv3-sources-table { background: rgba(10,10,10, 0.025); }
.qv3-root .qv3-light .qv3-table { background: transparent; }
/* Le bouton or/CTA conserve son contraste sur fond clair */
.qv3-root .qv3-light .qv3-btn-gold {
  background: #0A0A0A; color: #F7F7F5; border-color: #0A0A0A;
}
.qv3-root .qv3-light .qv3-btn-gold:hover { background: var(--red); border-color: var(--red); }
.qv3-root .qv3-light .qv3-link { color: #0A0A0A; }
.qv3-root .qv3-light .qv3-link:hover { color: var(--red); }
/* Marqueurs de section : conserver lisibilité */
.qv3-root .qv3-light .qv3-section-mark { color: #5A5A5A; }

/* ===================== TARIFS ===================== */
.qv3-tarifs { padding: 96px 0; background: #000; border-top: 1px solid var(--hairline); }
.qv3-tarifs-grid {
  list-style: none; padding: 0; margin: 40px 0 0;
  display: grid; gap: 20px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.qv3-tarif {
  position: relative;
  display: flex; flex-direction: column; gap: 14px;
  padding: 28px 22px;
  border: 0.5px solid var(--hairline);
  background: rgba(255,255,255,0.02);
}
.qv3-tarif-highlight {
  border-color: var(--nacre);
  box-shadow: 0 0 0 1px #F5FF00, 0 20px 40px -20px rgba(245,255,0,0.35);
}
.qv3-tarif-badge {
  position: absolute; top: -10px; left: 16px;
  background: #F5FF00; color: #000;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
  padding: 4px 10px;
}
.qv3-tarif-nom {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase;
  margin: 0; color: var(--nacre);
}
.qv3-tarif-prix {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-size: 34px; line-height: 1.05; margin: 0; color: var(--nacre);
}
.qv3-tarif-per {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px; letter-spacing: 0.08em; color: var(--grey-500);
  margin-left: 6px;
}
.qv3-tarif-inclus {
  font-family: "Fraunces", Georgia, serif; font-style: italic;
  font-size: 15px; color: var(--grey-300); margin: 0; flex: 1;
}
.qv3-tarif-cta {
  display: inline-block; text-align: center; margin-top: 8px;
  padding: 12px 18px; border: 0.5px solid #F5FF00; color: var(--nacre);
  text-decoration: none; background: transparent;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
  cursor: pointer;
}
.qv3-tarif-cta:hover { background: rgba(245,255,0,0.08); }
.qv3-tarif-cta-soon {
  border-color: var(--grey-700); color: var(--grey-500);
  cursor: not-allowed; opacity: 0.7;
}
.qv3-tarif-cta-soon:hover { background: transparent; }

/* ===================== FAQ ===================== */
.qv3-faq { padding: 96px 0; background: #000; border-top: 1px solid var(--hairline); }
.qv3-faq-list {
  margin-top: 40px;
  display: flex; flex-direction: column; gap: 12px;
  max-width: 820px; margin-left: auto; margin-right: auto;
}
.qv3-faq-item {
  border: 0.5px solid var(--hairline);
  background: rgba(255,255,255,0.02);
  transition: border-color 200ms ease, box-shadow 200ms ease;
}
.qv3-faq-item.is-open { border-color: rgba(245,255,0,0.5); }
.qv3-faq-h { margin: 0; font-weight: inherit; }
.qv3-faq-q {
  width: 100%;
  appearance: none; background: transparent; border: 0; margin: 0;
  text-align: left; cursor: pointer;
  display: flex; align-items: center; justify-content: space-between; gap: 20px;
  padding: 22px 24px;
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-size: 18px; line-height: 1.35; color: var(--nacre);
  border-radius: 2px;
  transition: color 180ms ease, background-color 180ms ease, box-shadow 180ms ease;
}
.qv3-faq-q:hover { color: var(--nacre); background-color: rgba(245,255,0,0.05); }
.qv3-faq-q:active { background-color: rgba(245,255,0,0.12); }
/* Focus ring FAQ — identique open/closed, indépendant du reset global.
   Utilise box-shadow (composable) plutôt que outline pour éviter les overrides. */
.qv3-faq-item .qv3-faq-q:focus { outline: none !important; }
.qv3-faq-item .qv3-faq-q:focus-visible,
.qv3-faq-item.is-open .qv3-faq-q:focus-visible {
  outline: none !important;
  box-shadow:
    0 0 0 2px #000,
    0 0 0 4px #F5FF00,
    0 0 0 8px rgba(245,255,0, 0.28) !important;
  border-radius: 2px;
}
.qv3-faq-item:focus-within {
  border-color: rgba(245,255,0, 0.7);
}


/* ===================== Focus global (landing) ===================== */
.qv3-root a:focus-visible,
.qv3-root button:focus-visible,
.qv3-root [role="button"]:focus-visible,
.qv3-root summary:focus-visible {
  outline: 2px solid #F5FF00;
  outline-offset: 3px;
  box-shadow: 0 0 0 5px rgba(245,255,0,0.28);
  border-radius: 2px;
  transition: box-shadow 160ms ease, outline-offset 160ms ease;
}
.qv3-root a:hover,
.qv3-root button:not(:disabled):hover { transition: color 160ms ease, background-color 160ms ease, border-color 160ms ease; }
.qv3-root a:active,
.qv3-root button:not(:disabled):active { transform: translateY(1px); }

@media (prefers-reduced-motion: reduce) {
  .qv3-faq-q,
  .qv3-root a:focus-visible,
  .qv3-root button:focus-visible,
  .qv3-root [role="button"]:focus-visible,
  .qv3-root summary:focus-visible,
  .qv3-root a:hover,
  .qv3-root button:not(:disabled):hover { transition: none; }
  .qv3-root a:active,
  .qv3-root button:not(:disabled):active { transform: none; }
}

.qv3-faq-chevron {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 22px; line-height: 1; color: var(--nacre); flex-shrink: 0;
  transition: transform 220ms ease;
  display: inline-block;
}
.qv3-faq-item.is-open .qv3-faq-chevron { transform: rotate(45deg); }
.qv3-faq-a {
  padding: 0 24px 24px;
  font-family: "Fraunces", Georgia, serif;
  font-size: 16px; line-height: 1.65; color: var(--grey-300);
  animation: qv3-faq-open 220ms ease both;
}
.qv3-faq-a[hidden] { display: none; }
.qv3-faq-a p { margin: 0; }
@keyframes qv3-faq-open {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .qv3-faq-a { animation: none; }
  .qv3-faq-chevron { transition: none; }
}

@media (max-width: 640px) {
  .qv3-tarifs, .qv3-faq { padding: 64px 0; }
  .qv3-faq-q { font-size: 16px; padding: 18px 18px; }
  .qv3-faq-a { padding: 0 18px 20px; font-size: 15px; }
}

/* ============ TUNNEL — post-IA verification ============ */
.qv3-tunnel-h1 {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-weight: 300;
  font-size: clamp(34px, 5.4vw, 64px);
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: var(--nacre);
  margin: 48px auto 20px;
  max-width: 780px;
}
.qv3-tunnel-h1 em {
  font-style: italic;
  color: var(--nacre);
}
.qv3-tunnel-tag {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 13px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--grey-300);
  margin: 0 auto 8px;
  max-width: 640px;
}

.qv3-tunnel {
  background: var(--ink);
  color: var(--nacre);
  padding: clamp(96px, 14vw, 180px) 0;
  border-top: 1px solid var(--hairline);
  position: relative;
}
.qv3-tunnel-wrap {
  max-width: 820px;
  text-align: center;
}
.qv3-tunnel-head {
  text-align: center;
  max-width: 820px;
  margin: 0 auto 72px;
}
.qv3-tunnel-h {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-weight: 300;
  font-size: clamp(32px, 4.8vw, 56px);
  line-height: 1.08;
  letter-spacing: -0.025em;
  margin: 0 0 32px;
  color: var(--nacre);
}
.qv3-tunnel-h em {
  font-style: italic;
  color: var(--nacre);
}
.qv3-tunnel-p {
  font-family: "Fraunces", "Playfair Display", Georgia, serif;
  font-weight: 300;
  font-size: clamp(17px, 1.7vw, 21px);
  line-height: 1.6;
  color: var(--grey-300);
  max-width: 680px;
  margin: 0 auto;
}

/* Process — 7 temps */
.qv3-process-list {
  list-style: none;
  padding: 0;
  margin: 0 auto;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.qv3-process-step {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 32px;
  align-items: baseline;
  padding: 32px 0;
  border-top: 1px solid var(--hairline);
}
.qv3-process-step:last-child { border-bottom: 1px solid var(--hairline); }
.qv3-process-num {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: clamp(28px, 3.4vw, 42px);
  font-weight: 300;
  color: var(--nacre);
  letter-spacing: 0.02em;
  line-height: 1;
}
.qv3-process-body { min-width: 0; }
.qv3-process-t {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 400;
  font-style: italic;
  font-size: clamp(20px, 2.2vw, 26px);
  color: var(--nacre);
  margin: 0 0 10px;
  letter-spacing: -0.01em;
}
.qv3-process-d {
  font-family: "Fraunces", Georgia, serif;
  font-size: 16px;
  line-height: 1.6;
  color: var(--grey-300);
  margin: 0;
}
@media (max-width: 640px) {
  .qv3-process-step { grid-template-columns: 56px 1fr; gap: 18px; padding: 24px 0; }
}

/* Deux portes */
.qv3-doors {
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  max-width: 980px;
  margin: 0 auto;
}
@media (min-width: 820px) {
  .qv3-doors { grid-template-columns: 1fr 1fr; gap: 40px; }
}
.qv3-door {
  border: 1px solid var(--hairline);
  padding: clamp(32px, 4vw, 56px);
  background: rgba(255,255,255,0.015);
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: border-color 240ms ease, background 240ms ease;
}
.qv3-door:hover { border-color: var(--nacre); background: rgba(245,255,0,0.03); }
.qv3-door-num {
  font-family: "Playfair Display", Georgia, serif;
  font-style: italic;
  font-weight: 300;
  font-size: 40px;
  color: var(--nacre);
  line-height: 1;
  margin-bottom: 8px;
}
.qv3-door-t {
  font-family: "Playfair Display", Georgia, serif;
  font-weight: 400;
  font-size: clamp(22px, 2.4vw, 28px);
  color: var(--nacre);
  margin: 0;
  letter-spacing: -0.015em;
}
.qv3-door-d {
  font-family: "Fraunces", Georgia, serif;
  font-size: 16px;
  line-height: 1.6;
  color: var(--grey-300);
  margin: 0;
  flex: 1;
}
.qv3-door-cta { margin-top: 12px; align-self: flex-start; }

/* ============ FLUO TOKENS (usage sémantique, par touches) ============ */
.qv3-root {
  --fluo-red: #FF1744;         /* tension / danger / risque */
  --fluo-green: #00E676;       /* validation / ce qui tient */
  --fluo-blue: #29B6F6;        /* analyse / rigueur / sources */
  --fluo-fuchsia: #FF00A8;     /* CTA / moments-clés */
}
.qv3-root .qv3-light {
  --fluo-red: #B3121F;
  --fluo-green: #007A3D;
  --fluo-blue: #006D99;
}
.fluo-red { color: var(--fluo-red); font-style: normal; }
.fluo-green { color: var(--fluo-green); font-style: normal; }
.fluo-blue { color: var(--fluo-blue); font-style: normal; }
.fluo-fuchsia { color: var(--fluo-fuchsia); font-style: normal; }
.fluo-red-strong { color: var(--fluo-red); font-weight: 400; }
.fluo-green-strong { color: var(--fluo-green); font-weight: 400; }
.fluo-u-blue {
  background-image: linear-gradient(var(--fluo-blue), var(--fluo-blue));
  background-repeat: no-repeat;
  background-position: 0 92%;
  background-size: 100% 3px;
  padding-bottom: 4px;
}

/* Bouton CTA fuchsia — moments-clés du tunnel */
.qv3-btn-fuchsia {
  display: inline-block;
  font-family: "Playfair Display", Georgia, serif;
  font-size: 15px; letter-spacing: 0.08em;
  color: #000000; text-decoration: none;
  padding: 18px 34px;
  background: #FFFFFF;
  border: 1px solid #FFFFFF;
  box-shadow: 0 0 0 0 rgba(0,0,0,0.25);
  transition: transform 220ms ease, box-shadow 260ms ease, background 220ms ease;
}
.qv3-btn-fuchsia:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 36px -12px rgba(0,0,0,0.45);
  background: #F5EFE3;
  color: #000000;
}
.qv3-root .qv3-light .qv3-btn-fuchsia { color: #000000; }

/* Phrase de verdict / bascule (isolée, plus grande) */
.qv3-tunnel-verdict-line {
  font-family: "Playfair Display", "Fraunces", Georgia, serif;
  font-weight: 300;
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1.25;
  letter-spacing: -0.015em;
  margin: 56px auto 0;
  max-width: 720px;
  color: var(--nacre);
}
.qv3-tunnel-verdict-line em { font-style: italic; color: var(--nacre); }

/* Titre citation (Section 3) */
.qv3-tunnel-quote { font-style: italic; }
.qv3-tunnel-quote em { font-style: italic; }

/* Climax (Section 6) */
.qv3-tunnel-h--climax { font-size: clamp(38px, 5.6vw, 68px); }

/* Anaphore "Là où l'IA..." (Section 4) */
.qv3-anaphore {
  list-style: none; padding: 0; margin: 48px auto 0;
  max-width: 760px;
  display: flex; flex-direction: column; gap: 20px;
  text-align: left;
}
.qv3-anaphore li {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  padding: 20px 24px;
  border-left: 2px solid var(--hairline);
}
.qv3-anaphore-lhs {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--grey-500);
}
.qv3-anaphore-rhs {
  font-family: "Fraunces", "Playfair Display", Georgia, serif;
  font-weight: 300;
  font-size: clamp(18px, 1.9vw, 22px);
  line-height: 1.55;
  color: var(--nacre);
}
@media (min-width: 720px) {
  .qv3-anaphore li { grid-template-columns: 240px 1fr; gap: 28px; align-items: baseline; }
}

/* Process — accents fluo par étape */
.qv3-process-num--blue { color: var(--fluo-blue); }
.qv3-process-num--red { color: var(--fluo-red); }
.qv3-process-num--green { color: var(--fluo-green); }
.qv3-process-step--red { border-top-color: rgba(255,23,68,0.35); }
.qv3-process-step--green { border-top-color: rgba(0,230,118,0.4); }
.qv3-process-step--red + .qv3-process-step,
.qv3-process-step--green + .qv3-process-step { border-top-color: var(--hairline); }
.qv3-process-flow .qv3-process-step--red .qv3-process-t,
.qv3-process-flow .qv3-process-step--green .qv3-process-t { font-style: italic; }

/* Doors — bordures fluo sémantiques */
.qv3-door--blue { border-color: rgba(41,182,246,0.55); }
.qv3-door--blue:hover { border-color: var(--fluo-blue); background: rgba(41,182,246,0.04); }
.qv3-door--green { border-color: rgba(0,230,118,0.55); }
.qv3-door--green:hover { border-color: var(--fluo-green); background: rgba(0,230,118,0.04); }
.qv3-door--blue .qv3-door-num { color: var(--fluo-blue); }
.qv3-door--green .qv3-door-num { color: var(--fluo-green); }

/* Section 3 (cas phare) — fond nacré, respire */
.qv3-tunnel-cas { padding: clamp(112px, 15vw, 200px) 0; }

/* Section 8 (CTA final) — épuré, gravité */
.qv3-tunnel-final { padding: clamp(120px, 16vw, 200px) 0; }
`;
